import type { Product } from '@meitu/subscribe';
import type { AssetListTaskTypeFilterValue } from '$/constants/assetListTaskTypeFilter';
import type { FileType } from '$/utils/upload';
import { ModelPatternInfoItem } from '$/domains/agentModel/type';

interface ResponseAgentModel {
  model_desc: string;
  model_name: string;
  model_value: string;
  model_title?: string;
  badge_url?: string;
  enable?: boolean;
  icon?: string;
}

export interface ResponseMarkPointInfo {
  error_code: number;
  parameter: {
    object_list: {
      name: string;
      type: number[];
      box: {
        x: number;
        y: number;
        w: number;
        h: number;
      };
    }[];
  };
}
export interface ResponseAgentModelCategory {
  model_category_title: string;
  model_items: Array<ResponseAgentModel>;
}

export interface CategoryList {
  child_category: string;
  child_category_type: number;
  unit: string;
  coin_cost_num: number;
  high_pattern_min_cost: number;
  high_pattern_max_cost: number;
  single_description: string;
}
export interface ResponseRoomItemModelList {
  error_code: number;
  parameter: {
    image_model: ResponseAgentModelCategory;
    video_model: ResponseAgentModelCategory;
    interface_model?: ResponseAgentModelCategory;
    menu_detail: {
      model_pattern_info: ModelPatternInfoItem[];
      category_list: CategoryList[];
      selected_model_pattern: 'normal' | 'high';
    };
    thinking_mode_info?: {
      default_mode_value: string;
      mode_items: any[];
    };
  };

}
export interface ResponseRoomItemInfo {
  error_code: number;
  parameter: {
    used_model: {
      image_model: ResponseAgentModel;
      video_model: ResponseAgentModel;
      interface_model?: ResponseAgentModel;
    };
  };
}

export interface ResponseHistoryMediaItem {
  media_type: 'image' | 'video';
  media_url: string;
  media_url_by_water_mark: string;
  /** 视频压缩地址；仅视频历史记录可能下发，用于再次添加到输入框或画布时透传。 */
  media_url_by_compress?: string;
  /** 视频水印压缩地址；仅视频历史记录可能下发，用于再次添加到输入框或画布时透传。 */
  media_url_by_water_mark_compress?: string;
  media_cover_url: string;
  media_cover_url_by_water_mark: string;
  width: number;
  height: number;
  duration: number;
  ori_media_url: string;
  ori_media_urls: string[];
  size: number;
  source_media_type: string;
  task_id: string;
  record_id: string;
  /** 服务端可能预下发的资产 id；插入后仍会优先通过 createAsset 入库，仅作回退或扩展字段 */
  asset_id?: number;
}

export interface ResponseHistoryMusicItem {
  content: string;
  content_title: string;
  music_url: string;
  music_url_watermark: string;
  cover_url: string;
  duration: number;
  lyrics: string;
  music_fail_message: string;
  desc: string;
  task_id: string;
  record_id: string;
}

export interface ResponseHistoryCardDetail {
  id: string;
  content: string;
  is_welcome?: boolean;
  peer_type: string;
  is_uncompleted: boolean;
  create_at: number;
  create_time: number;
  design_id: string;
  message_type: number;
  room_id: string;
  room_type: number;
  stream_id: string;
  task_id: string;
  title: string;
  title_icon: string;
  type: string;
  media_items?: ResponseHistoryMediaItem[];
  music_items?: ResponseHistoryMusicItem[];
  child_cards?: ResponseHistoryCardDetail[];
  [key: string]: any;
}

export interface ResponseHistoryItem {
  card_detail: ResponseHistoryCardDetail;
  role: 'user' | 'assistant';
}
export interface ResponseHistory {
  error_code: number;
  parameter: {
    items: any[];
    is_max: boolean;
    room_title: string;
    is_complete: boolean;
    user_cancel: boolean;
    share_valid?: number;
    cancel_in_half: boolean;
    used_model: {
      image_model: ResponseAgentModel;
      video_model: ResponseAgentModel;
      interface_model?: ResponseAgentModel;
      thinking_mode: {
        mode_value: string;
        mode_name: string;
        icon?: string;
        mode_desc: string;
        mode_title: string;
      };
    };
    extra: Record<string, any>;
    model_pattern: {
      name: string;
      type: 'normal' | 'high';
    };
  };

}
export interface UploadSignParams {
  parameter: {
    count: number;
    suffix: string;
    app: string;
    type: string;
  };
}

export interface CheckFileParams {
  parameter: {
    path_scene: string;
    media_urls: string[];
  };
}

export interface DefaultLayerParams {
  room_id: string;
}

export interface LayerItem {
  layer_id: string;
  editor_params: string;
}

export interface SaveLayerParams extends DefaultLayerParams {
  layers: LayerItem[];
}
export interface DeleteLayerParams extends DefaultLayerParams {
  layer_ids: string;
}

export interface LayerItemResult {
  layer_id: string;
  params: string;
}
export enum ProjectType {
  Canvas = 1,
  Workflow = 2,
  AgentTeam = 5,
}
export interface GetRoomParams {
  room_type?: ProjectType;
  agent?: string;
}

export interface SubmitRoomParams {
  room_id: string;
  room_type: ProjectType;
}
export interface GetRoomResponse {
  parameter: {
    room_id: string;
  };
}

/**
 * 任务类型枚举
 *
 * @export
 * @enum {number}
 */
export enum TaskCategory {
  LAYERING = 'layering', // 图片分层
  MAGICSR = 'magicsr', // AI超清
  CUTOUT = 'cutout', // 抠图
  INPAINT = 'inpaint', // 改图
  KONTEXT_INPAINT = 'kontext_inpaint', // kontext改图
  ERASER = 'eraser', // AI消除
  EXTEND = 'extend', // AI 扩图
  AIPPT = 'ai_ppt', // ai会话 ppt
  MODIFY_TEXT = 'text_editing',
  PNG2SVG = 'png2svg',
  FUSION = 'fusion', // 融图
  AI_FACE = 'ai_face', // 人脸检测
  AUDIO_DENOISE = 'ai_audio_spliter', // 音频降噪
}

/** 工具类型 tool_type：task_type=tool 时使用，与 webapi 任务回调一一对应 */
export enum AssetToolType {
  PNG2SVG = 'png2svg',
  IMAGE_FUSION = 'image_fusion',
  IMAGE_CUTOUT = 'image_cutout',
  IMAGE_INTERACTSEG = 'image_interactseg',
  IMAGE_UPSCALER = 'image_upscaler',
  TEXT_EDITING = 'text_editing',
  IMAGE_INPAINT = 'image_inpaint',
  IMAGE_ERASER = 'image_eraser',
  IMAGE_EXTENSION = 'image_extension',
  IMAGE_LAYERING = 'image_layering',
  IMAGE_GLASSES = 'image_glasses',
  IMAGE_PEOPLE = 'image_people',
  IMAGE_WATERMARK = 'image_watermark',
  VIDEO_WATERMARK = 'video_watermark',
  VIDEO_HD = 'video_hd',
  MERGE_LAYERS = 'merge_layers',
  LOCAL_CUTOUT = 'local_cutout',
}

// 图片状态，1-正常，2-审核异常
export enum ImageStatus {
  NORMAL = 1,
  CHECK_ABNORMAL,
}

// 透明底图alpha通道状态，值0：不存在alpha通道，值1：存在alpha通道，值2：alpha通道已替换结果；
// 透明地图超清算法返回的结果图不是透明的，需要前端处理成透明的，通过替换接口替换下
export enum AlphaStatus {
  NONENTITY,
  EXIST_ALPHA,
  ALPHA_RESULT,
}

// 任务在画布中的使用状态，1-未使用，2-使用
export enum UseStatus {
  UN_USE = 1,
  USED,
}

interface ResultImagesItem {
  url_short: string;
  url_sign: string;
  url_water_sign: string;
  watermark_url: string;
  image_status: ImageStatus;
  width: number;
  alpha_status: AlphaStatus;
  height: number;
}

export interface TaskQueryResult {
  id: string;
  layer_id: string;
  task_category: TaskCategory;
  fail_msg: string;
  status: number;
  init_image: string[];
  result_images: ResultImagesItem[];
  result: string;
  use_status: UseStatus;
}

export interface FontFamilyItem {
  id: number;
  font_id: number;
  name: string;
  default_name: string;
  font_file: string;
  preview_file: string;
  wof_font_file: string;
  zip_font_file: string;
}

export interface ShareDetailResult {
  is_max: boolean;
  room_title: string;
  share_author_avatar: string;
  share_author_name: string;
  share_cover?: string;
  items: any[];
  room_id: string;
}

export interface SubmitFeedbackParams {
  room_id: string;
  task_id: string;
  param: string;
  content: string;
}

// 改图提交任务参数
export interface InpaintParams {
  init_image: string; // 原图
  mask_image: string; // mask图
  prompt: string; // 提示词，非必填
  batch_size?: number; // 生成数量，非必填
}

export interface ModifyTextParams {
  init_image: string; // 原图
  media_list: {
    mask_image: string; // mask图
    text: string; // 替换文字
  }[];
  preserve_alpha?: number; // 是否保留透明通道，1保留，0不保留；客户端需要判断下原图是否带有透明通道，有透明通道传1，没有透明通道传0
  batch_size?: number; // 生成数量，非必填
}

// AI消除提交任务参数
export interface EraserParams {
  init_image: string; // 原图
  mask_image: string; // mask图
}

// 编辑图片美叶价格
export type EditImagePriceItem = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [_key in TaskCategory]: number;
};

export interface FusionImageParams {
  init_images: string[];
  prompt: string;
}

// 扩图提交任务参数
export interface ExtendImagePrams {
  image_file: string; // 原图
  width: number; // 目标宽度
  height: number; // 目标高度
  scale: number; // 图片缩放比例
  center_x: number; // 中心水平偏移量
  center_y: number; // 中心垂直偏移量
  prompt?: string; // 提示词，非必填
}

export interface CommonConfigInfo {
  bottom: boolean;
  enable: boolean;
  is_common: boolean;
  name: string;
  style: number;
  type: number;
}

export interface HomeTag {
  name: string;
  desc: string;
  id: number;
  example_media: {
    url: string;
    type: FileType;
    width: number;
    height: number;
    duration: number;
    cover: string;
  }[];
}

/**
 * 热门prompt分类
 */
export interface PromptGroup {
  id: number;
  name: string;
  icon: string;
}

/**
 * 热门prompt媒体项
 */
export interface HotPromptMedia {
  url: string;
  cover: string;
  type: string;
  width: number;
  height: number;
  duration: number;
  can_delete?: number;
  can_replace?: number;
}

/**
 * 热门prompt项 (from /common/home_prompt.json API)
 */
export interface HotPromptItem {
  id: number;
  name: string;
  desc: string;
  /** 封面图列表 */
  cover_list?: string[];
  /** skill id */
  sop_id?: number;
  icon?: string;
  example_media: HotPromptMedia[];
}

/**
 * 基础配置参数
 */
export interface CommonConfigParams {
  show: boolean;
  input: CommonConfigInfo[];
  home_tags: HomeTag[];
  prompt_group?: PromptGroup[];
}

/**
 * 卡片列表参数
 *
 * @export
 * @interface CardsParams
 */
export interface CardsParams {
  page: number;
  count: number;
  category_id?: number;
}

export interface CardListItem {
  card_id: number;
  card_type: 1001;
  cover: string;
  link: string;
  media: {
    url: string;
    type: string;
    width: number;
    height: number;
    duration: number;
  };
  prompt: string;
  title: string;
  used_num: string;
  user: {
    screen_name: string;
    avatar: string;
    uid: number;
  };
}

export interface CategoryListItem {
  category_id: number;
  default: boolean;
  name: string;
  selected: boolean;
  weight: number;
}

/**
 * 卡片列表返回数据
 *
 * @export
 * @interface CardsParams
 */
export interface CardsResult {
  card_list: CardListItem[];
  category_list: CategoryListItem[];
  total: number;
}

/**
 * 保存节点
 *
 * @export
 * @interface SaveWorkFlowParams
 */
export interface SaveWorkFlowParams {
  room_id: string;
  data: string;
}

/**
 * 画布数据
 *
 * @export
 * @interface GetWorkFlowInfoResult
 */
export interface GetWorkFlowInfoResult {
  room_id: string;
  name: string;
  data: string;
}

/**
 *  萝卜订单明细
 */
export interface RadishDetailItem {
  title: string;
  content: string;
  register_send_num: string;
  btn_text: string;
  scene: string;
}

/**
 * 登陆赠送萝卜接口
 */

export interface RadishConfig {
  title: string;
  content: string;
  send_num: string;
  btn_text: string;
  scene: string;
}

export interface UserRadishRecordItem {
  record_id: string;
  room_id: string;
  room_title: string;
  amount: number;
  consume_time: string;
  scene_name: string;
  reduce_media_num: number;
  unit: string;
  /** 房间类型，见 {@link ProjectType} */
  room_type: ProjectType;
  del_status: 0 | 1; // 0 正常 1 已删除
}
export interface CanvasErrmsg {
  reduce_fail_guide: string;
}

/** 商品权益单条说明，气泡文案由接口提供。 */
export interface ProductRightsSubTipItem {
  id: string;
  text: string;
  bubbleText?: string;
}

export interface ProductRightsDescItem {
  title?: string;
  subTitle?: string;
  titleIcon?: string;
  itemCategory?: string;
  subTips?: string[];
  subTipItems?: ProductRightsSubTipItem[];
}

export interface ProductTitleContent {
  icon?: string;
  coinNum?: string | number;
  coin_num?: string | number;
  storageLimit?: string | number;
  storage_limit?: string | number;
  titlePrefix?: string;
  title_prefix?: string;
  titleSuffix?: string;
  title_suffix?: string;
  completeTitle?: string;
  complete_title?: string;
  unitCoinPrice?: string;
  unit_coin_price?: string;
}

export interface ProductPromotionGift {
  commodityConfig?: {
    count?: string | number;
  };
  commodity_config?: {
    count?: string | number;
  };
}

export interface ProductPromotionDisplay {
  promotionType?: string | number;
  promotion_type?: string | number;
  promotionPriceText?: string;
  promotion_price_text?: string;
}

/** snake_case 响应经 deepKeysToCamelCase 转换后的团队商品席位信息。 */
export interface ProductSeatInfo extends Record<string, unknown> {
  lowerLimit: number;
  upperLimit: number;
  seatTotalNum: number;
  seatUsedNum: number;
  addPurchasePrice: number;
  moneyUnit: string;
  moneySymbol: string;
  mainExplain: string;
}

export interface SuperProduct extends Product {
  subCommodityBo: {
    level: number;
  };
  subPeriod?: Product['subPeriod'];
  sub_period?: string | number;
  subPeriodDuration?: number;
  sub_period_duration?: string | number;
  subPeriodDesc?: string;
  sub_period_desc?: string;
  memberLabelName?: string;
  member_label_name?: string;
  productName: string;
  product_name?: string;
  productPriceExplain?: string;
  product_price_explain?: string;
  productTitleContent?: ProductTitleContent;
  product_title_content?: ProductTitleContent;
  promotionGifts?: ProductPromotionGift[];
  promotion_gifts?: ProductPromotionGift[];
  productPromotionDisplay?: ProductPromotionDisplay;
  product_promotion_display?: ProductPromotionDisplay;
  productRightsDesc?: ProductRightsDescItem[];
  seatInfo?: ProductSeatInfo;

}

export interface CoreModelPriceTableItem {
  modelType?: string;
  modelTypeLabel?: string;
  modelName?: string;
  modelIconKey?: string;
  consumeUnit?: string;
  consumeUnitLabel?: string;
  consumeCount?: number;
  price?: number;
  levelPrices?: CoreModelPriceTableMemberPrice[];
}

export interface CoreModelPriceTableMemberPrice {
  level?: number | string;
  memberName?: string;
  memberLabelName?: string;
  tokenUnitPrice?: number | string;
  consumeCount?: number | string;
  price?: number | string;
  displayText?: string;
}

export interface ProductExtTableHeader {
  key: string;
  label: string;
}

export interface CoreModelPriceTable {
  title?: string;
  headers?: ProductExtTableHeader[];
  items?: CoreModelPriceTableItem[];
  footer?: string;
}

export interface HighQualityShortDramaTableItem {
  dimension?: string;
  roboneoAgent?: string;
  traditional?: string;
  freelance?: string;
  diyAi?: string;
}

export interface HighQualityShortDramaTable {
  title?: string;
  headers?: ProductExtTableHeader[];
  items?: HighQualityShortDramaTableItem[];
}

export interface AgentTeamProductExt {
  coreModelPriceTable?: CoreModelPriceTable;
  highQualityShortDramaTable?: HighQualityShortDramaTable;
}

export interface VIPInfoList {
  products: SuperProduct[];
  productExt?: AgentTeamProductExt;
}

interface ActivityItem {
  id: number;
  smartcat_key: string;
  lang: string;
  activity_name: string;
  activity_link: string;
  created_at: number;
}
export interface ActivityList {
  list: ActivityItem[];
}

/** 弹窗媒体资源（图片 / PAG 等） */
export interface PopupMediaAsset {
  url: string;
  width?: number;
  height?: number;
  /** PAG 播放次数；-1 或未返回表示无限循环 */
  play_count?: number;
}

export interface PopupInfoResponse {
  show: boolean;
  popup: {
    id: string | number;
    title: string;
    end_time?: number;
    position: PopupPosition;
    ext: {
      img: PopupMediaAsset;
      /** PAG 动画资源；有 url 时优先加载 PAG */
      pag?: PopupMediaAsset;
      /** PAG 加载失败时的静态图兜底 */
      fallback_img?: PopupMediaAsset;
      close_btn: {
        position: 'right_top' | 'middle_bottom';
        type: number;
      };
      foreground?: {
        img: PopupMediaAsset;
      };
      background?: {
        img: PopupMediaAsset;
      };
      /** 当前营销位仅在该字段严格为 true 时展示倒计时 */
      show_countdown?: boolean;
      scheme: string;
      toast?: string;
      copy?: string;
    };
  }[];
  server_time?: number;
  v3_canvas_publish_enabled: boolean; // 是否开启v3画布发布能力
}

export enum PopupPosition {
  HOME = 'home',
  HOME_TOP_BANNER = 'home_top_banner',
  MEMBER_YINGXIAO_ALERT = 'member_yingxiao_alert',
  CANVAS_RIGHT_BOTTOM_SORA = 'canvas_right_bottom_sora',
  CANVAS_RIGHT_BOTTOM_BANANA = 'canvas_right_bottom_banana',
  WORKFLOW_RIGHT_BOTTOM_SORA = 'workflow_right_bottom_sora',
  WORKFLOW_RIGHT_BOTTOM_BANANA = 'workflow_right_bottom_banana',
}
export interface CheckAIFaceParams {
  init_image: string;
  mask_image: string;
  max_face_percent: number; // 选择区域占人脸区域的上限 默认值 1
  min_face_percent: number; // 选择区域占人脸区域的下限 默认值 0.5
  max_percent?: number; // 当选择区域包括人脸区域的时候 人脸区域占选择区域的上限 默认值 1
  min_percent?: number; // 当选择区域包括人脸区域的时候 人脸区域占选择区域的下限 默认值 0.3
}

/**
 * 仲裁模式-反馈退钱 请求参数
 */
export interface FeedbackDealParams {
  room_id?: string; // 房间id
  task_id?: string; // 该图的task_id值，用于定位任务
  media_url?: string; // 媒体url
  record_id?: string; // 记录id
  scene: 'pre_feedback_deal' | 'feedback_deal';
}

/**
 * 仲裁模式-反馈退钱 返回数据
 */
export interface FeedbackDealResponse {
  popup_data: {
    title: string; // 标题
    sub_title: string; // 子标题
    content: string; // 内容
    btn_text: string; // 按钮文案
    btn_text2: string; // 按钮文案2
    popup_type: string; // 【确认弹窗】confirm_pop, feedback_deal_pop【反馈弹窗】
    ui_type: 'double_check' | 'simple'; // 界面类型
  };
}
export interface CropIframe {
  frame_url: string;
}

// ========== 资产库 API 类型 ==========
/** 文件夹项 */
export type AssetFolderType = 'normal' | 'room';

/** 文件夹项 */
export interface AssetFolderItem {
  id: number;
  name: string;
  folder_type: AssetFolderType;
  /** room 类型文件夹对应业务房间 ID，普通文件夹可为空 */
  room_id?: string;
  is_pinned: number;
  pinned_at: number;
  asset_count: number;
  created_at: number;
}

/** 资产列表项：图片类 `image` 嵌套字段 */
export interface AssetItemImageMeta {
  width?: number;
  height?: number;
  /** 文件大小（字节），接口可能为 0 */
  size?: number;
  /** EXIF 方向等，接口多为字符串数字 */
  orientation?: string;
  format?: string;
}

/** 资产列表项：视频类 `video` 嵌套字段 */
export interface AssetItemVideoMeta {
  width?: number;
  height?: number;
  /** 編碼 metadata 顯示旋轉角（度），與 ffprobe side_data rotation 一致；90/270 時實際觀看寬高與編碼寬高對調 */
  rotation?: number;
  size?: number;
  duration?: number;
  frames?: number;
  fps?: number;
  format_name?: string;
  format_long_name?: string;
}

/** 资产列表项：音频类 `audio` 嵌套字段 */
export interface AssetItemAudioMeta {
  duration?: number;
  size?: number;
  format_name?: string;
  format_long_name?: string;
}

/**
 * 列表接口文档/办公类素材 `file.format` 常见取值（小写扩展名）。
 * 使用 `| string` 保留后端扩展。
 */
export type AssetListItemFileFormat =
  | 'doc'
  | 'docx'
  | 'pdf'
  | 'txt'
  | 'md'
  | 'ppt'
  | 'pptx'
  | 'xls'
  | 'xlsx'
  | 'csv'
  | string;

/**
 * 文档 / 表格 / PPT 等列表项上的 `file` 对象（与 `material_type` 为 document、excel、ppt 等对应）。
 * 接口可能仅下发 `url`，`file` 整体缺省；有则常见为 `size` + `format`。
 */
export interface AssetItemFileMeta {
  /** 文件大小（字节） */
  size?: number;
  /** 扩展名或小写格式，如 docx */
  format?: AssetListItemFileFormat;
}

/**
 * 列表接口 `task_type` / `source_type` 常见取值（与 `AssetListTaskTypeFilterValue` 对齐并含画布）。
 * 使用 `| string` 保留后端扩展。
 */
export type AssetListItemTaskSourceType =
  | 'agent'
  | 'agent-team'
  | 'workflow'
  | 'tool'
  | 'canvas'
  | 'ai_creations';

/** 列表接口 `material_type` 常见取值 */
export type AssetListItemMaterialType =
  | 'image'
  | 'video'
  | 'audio'
  | 'script'
  | 'document'
  | 'excel'
  | 'ppt';

/** 历史记录中一条剧本资产包含的单集文件；顺序即目录展示顺序。 */
export interface AssetScriptEpisode {
  episode_id: string;
  title: string;
  urls: string[];
}

export interface AssetScriptIpBinding {
  ip_id: number;
  ip_version: number;
}

/** 资产（素材）项 */
export interface AssetItem {
  id: number;
  /** 展示名；接口常返回空字符串，亦可能缺省 */
  name?: string;
  task_type: AssetListItemTaskSourceType | string;
  tool_type: string;
  material_type: AssetListItemMaterialType | string;
  source_type: AssetListItemTaskSourceType | string;
  source_id: string;
  client_id?: string;
  /** 业务房间 ID；房间视图按此字段合并为同一房间，缺省时回退 source_id 分组 */
  room_id?: string;
  /** 房间展示名；房间视图分组标题优先用此字段 */
  room_name?: string;
  url: string;
  /** 剧本历史记录的完整目录；卡片只预览首集首文件，不能丢弃其余剧集。 */
  episodes?: AssetScriptEpisode[];
  ip_bindings?: AssetScriptIpBinding[];
  file_size?: number;
  /** 有水印成品地址；列表返回时已加签。下载时若存在则弹出水印偏好弹窗，用户可选择带水印或原图下载 */
  watermark_url?: string;
  /** 视频压缩地址；仅视频资源可能下发，用于聊天标注与预览引用透传。 */
  compress_url?: string;
  /** 视频水印压缩地址；仅视频资源可能下发，用于聊天标注与预览引用透传。 */
  watermark_compress_url?: string;
  /** 历史记录接口兼容字段：视频压缩地址。 */
  media_url_by_compress?: string;
  /** 历史记录接口兼容字段：视频水印压缩地址。 */
  media_url_by_water_mark_compress?: string;
  thumbnail_url: string;
  is_favorited: number;
  created_at: number;
  /** 文档/表格/PPT 等列表接口可能下发的 `file`；部分条目无此字段 */
  file?: AssetItemFileMeta;
  /** 图片类嵌套元信息 */
  image?: AssetItemImageMeta;
  /** 视频类嵌套元信息 */
  video?: AssetItemVideoMeta;
  /** 音频类嵌套元信息 */
  audio?: AssetItemAudioMeta;
  /** 资源宽度（像素），列表接口可能下发 */
  width?: number;
  /** 资源高度（像素），列表接口可能下发 */
  height?: number;
  /** 视频/音频时长（秒），列表接口可能在根节点与嵌套对象中同时出现 */
  duration?: number;
  /** 部分列表项在根节点返回文件大小（字节） */
  size?: number;
  /** 原始展示文件名（含扩展名），与入库 `createAsset` 的 `name` 一致；缺省时前端由 URL 推断 */
  file_name?: string;
}

/** 文件夹列表响应 */
export interface AssetFolderListResponse {
  folders: AssetFolderItem[];
  total: number;
}

/** 提交打包下载任务参数：folder_id 与 asset_ids（非空）二选一；请求层会将 asset_ids 序列化为逗号分隔字符串 */
export type AssetFolderDownloadSubmitParams =
  | { folder_id: number; asset_ids?: never; with_watermark?: number; aigc?: 0 | 1; }
  | { asset_ids: number[]; folder_id?: never; with_watermark?: number; aigc?: 0 | 1; };

/** 按文件夹下载压缩包 - 提交任务响应 */
export interface AssetFolderDownloadSubmitResponse {
  task_id: string;
  status: string;
  /** 打包涉及的资产 id；文件夹下载等场景供埋点 `roboneo_asset_save_success.asset_id` 使用（部分环境 JSON 为 string[]） */
  asset_ids?: (number | string)[];
}

/** 文件夹下载任务状态（轮询结果） */
export interface AssetFolderDownloadStatusResponse {
  task_id: string;
  folder_id: number;
  status: string;
  file_name: string;
  download_url: string;
  error_msg: string;
  created_at: number;
  finished_at: number;
}

export interface CanvasMaterialLightingFileInfo {
  url: string;
  width?: number;
  height?: number;
  size?: number;
  bg_color?: string;
  md5?: string;
  name?: string;
}

export interface CanvasMaterialLightingItem {
  id: number | string;
  default_name: string;
  name?: string | string[];
  ai_prompt?: string;
  brightness?: string | null;
  direction?: string | null;
  lightColor?: string | null;
  rimLight?: boolean | number | string | null;
  rimLightDirection?: string | null;
  pic_file?: CanvasMaterialLightingFileInfo;
  preview?: CanvasMaterialLightingFileInfo;
  sort?: number;
  status?: number;
  show_status?: number;
  type?: string;
}

export interface CanvasMaterialLightingListResponse {
  cursor: string;
  list: CanvasMaterialLightingItem[];
}

export interface CanvasPromptTemplateItem {
  id: string;
  model: string;
  prompt: string;
  visible: boolean;
  title?: string;
  name?: string;
  default_name?: string;
}

export interface CanvasPromptTemplateListParams {
  visible_only?: 0 | 1;
  ids?: string;
  app_id?: string;
  client_id?: string;
}

export interface CanvasPromptTemplateListResponse {
  list: CanvasPromptTemplateItem[];
  total: number;
}

/** 资产列表查询参数 */
export interface AssetListParams {
  /** 精确读取一条历史记录；响应仍为 list。 */
  asset_id?: number;
  folder_id?: number;
  /** 团队历史记录创建人 UID；使用字符串可避免 int64 精度丢失 */
  created_by_uid?: number | string;
  task_type?: AssetListTaskTypeFilterValue;
  tool_type?: AssetToolType | string;
  material_type?: 'all' | 'image' | 'video' | 'audio' | 'script' | 'document' | 'excel' | 'ppt';
  time_start?: string;
  time_end?: string;
  only_favorited?: number;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/** 资产库关键词搜索请求体（POST）；筛选字段与 `AssetListParams` 一致，并必填 `keyword` */
export interface AssetSearchParams extends AssetListParams {
  keyword: string;
}

/** 资产列表响应 */
export interface AssetListResponse {
  list: AssetItem[];
  total: number;
}

/** 创建资产时的来源场景；仅允许后端约定的枚举值，否则接口参数错误 */
export type AssetSourceScene = 'task_generate' | 'import';

/** 创建资产（用户端）参数 */
export interface AssetCreateParams {
  task_type: 'workflow' | 'tool' | 'agent' | 'agent-team' | 'ai_creations';
  tool_type?: AssetToolType | string;
  material_type: 'image' | 'video' | 'audio' | 'document' | 'excel' | 'ppt';
  source_id?: string;
  /** 任务 ID，写入资产表 task_id */
  task_id?: string;
  /** 来源场景，写入 source_scene */
  source_scene?: AssetSourceScene;
  url: string;
  /** 有水印成品地址，写入 ext.watermark_url；列表返回时加签。无水印下载用 url，有水印下载优先 watermark_url */
  watermark_url?: string;
  /** 视频压缩地址，写入 ext.compress_url；列表返回时用于标注和低清预览透传 */
  compress_url?: string;
  /** 视频水印压缩地址，写入 ext.watermark_compress_url；列表返回时用于标注和低清预览透传 */
  watermark_compress_url?: string;
  thumbnail_url?: string;
  ext?: string;
  /** 资源宽度（像素） */
  width?: number;
  /** 资源高度（像素） */
  height?: number;
  /** 时长（秒，如视频/音频） */
  duration?: number;
  /** 房间 ID */
  room_id?: string;
  /** 房间名称 */
  room_name?: string;
  /** 原始文件名（含扩展名），资产库列表展示与之一致；请求体字段名为 `name` */
  name?: string;
}

/** 批量操作 action_type */
export type AssetBatchActionType =
  | 'star'
  | 'unstar'
  | 'delete'
  | 'add_to_folder'
  | 'remove_from_folder';

// ========== 素材库 v3.1 API 类型 ==========
/** 素材库系统默认文件夹 key。 */
export type MediaLibrarySystemKey = 'role' | 'gen_role' | 'scene' | 'prop' | 'style' | 'sfx' | 'other';

/** 素材库文件夹类型：系统默认夹或用户自建夹。 */
export type MediaLibraryFolderKind = 'system_default' | 'user';

/** 素材库支持保存的素材类型。 */
export type MediaLibraryMaterialType = 'text' | 'image' | 'video' | 'audio' | 'script';

/** 素材库文件夹树节点，接口首次调用会懒初始化系统默认夹。 */
export interface MediaLibraryFolderTreeNode {
  id: number;
  parent_id: number;
  name: string;
  folder_kind: MediaLibraryFolderKind | string;
  system_key?: MediaLibrarySystemKey | string;
  depth: number;
  /** 同父级展示顺序，folder/tree 已按该字段升序返回。 */
  sort_order?: number;
  /** 当前文件夹下素材数量，部分环境可能返回 item_count。 */
  asset_count?: number;
  item_count?: number;
  children?: MediaLibraryFolderTreeNode[];
}

/** 素材库文件夹树返回的独立业务入口，例如角色 IP 分类。 */
export interface MediaLibraryFolderTreeEntry {
  key: string;
  name: string;
  fixed?: boolean;
  deletable?: boolean;
  movable?: boolean;
  renamable?: boolean;
  list_api?: string;
  list_params?: Record<string, string>;
}

/** 素材库文件夹树响应。 */
export interface MediaLibraryFolderTreeResponse {
  list: MediaLibraryFolderTreeNode[];
  /** 独立业务入口不属于普通文件夹，不能转换成 folder_id。 */
  entries?: MediaLibraryFolderTreeEntry[];
}

/** 素材库创建文件夹参数。 */
export interface MediaLibraryFolderCreateParams {
  parent_id?: number;
  name: string;
}

/** 素材库创建文件夹响应。 */
export interface MediaLibraryFolderCreateResponse {
  folder_id: number;
}

/** 素材库移动文件夹参数；target_parent_id=0 表示移动到根下，sort_order 从 1 开始，不传则追加到末尾。 */
export interface MediaLibraryFolderMoveParams {
  folder_id: number;
  target_parent_id: number;
  sort_order?: number;
}

/** 素材库文件夹移动响应；大子树移动会返回异步任务。 */
export interface MediaLibraryFolderMoveResponse {
  async?: boolean;
  task_id?: string;
}

export type MediaLibraryFolderMoveTaskStatus = 'pending' | 'running' | 'done' | 'failed';

/** 素材库文件夹异步移动状态响应。 */
export interface MediaLibraryFolderMoveStatusResponse {
  status: MediaLibraryFolderMoveTaskStatus;
  message?: string;
}

/** 素材库素材保存参数，gen_params/ext 均为 JSON 字符串。 */
export interface MediaLibraryItemSaveParams {
  folder_id: number;
  name: string;
  material_type: MediaLibraryMaterialType;
  url: string;
  thumbnail_url?: string;
  compress_url?: string;
  watermark_compress_url?: string;
  gen_params?: string;
  ext?: string;
  file_size?: number;
}

/** 素材库素材保存响应。 */
export interface MediaLibraryItemSaveResponse {
  item_id: number;
}

/** 素材库列表查询参数。 */
export interface MediaLibraryItemListParams {
  folder_id?: number;
  material_type?: MediaLibraryMaterialType | string;
  /** 团队态按素材创建成员筛选，使用数字字符串兼容 int64 UID。 */
  created_by_uid?: number | string;
  page?: number;
  page_size?: number;
}

/** 素材库列表项。 */
export interface MediaLibraryItem {
  id: number;
  folder_id: number;
  name: string;
  material_type: MediaLibraryMaterialType | string;
  url: string;
  thumbnail_url?: string;
  /** 视频压缩地址；服务端可能在根节点或 ext 中返回。 */
  compress_url?: string;
  /** 视频水印压缩地址；服务端可能在根节点或 ext 中返回。 */
  watermark_compress_url?: string;
  gen_params?: string;
  ext?: string;
  file_size?: number;
  created_at: number;
}

/** 素材库列表响应。 */
export interface MediaLibraryItemListResponse {
  list: MediaLibraryItem[];
  total: number;
}

/** 素材库批量操作类型。 */
export type MediaLibraryItemBatchActionType = 'delete' | 'move';

/** 素材库批量操作参数。 */
export interface MediaLibraryItemBatchActionParams {
  action_type: MediaLibraryItemBatchActionType;
  item_ids: number[];
  target_folder?: number;
}

/** 素材库存储占用响应。 */
export interface MediaLibraryStorageUsageResponse {
  total_bytes: number;
}

/** 团队存储联系信息保存请求参数 */
export interface TeamStorageContactSaveParams {
  /** 联系邮箱 */
  email: string;
  /** 本次上传所需空间（字节） */
  required_bytes: number;
  /** 场景标识 */
  scene: string;
}

/** 团队存储额度信息响应 */
export interface TeamStorageInfoResponse {
  /** 已使用字节数 */
  used_bytes: number;
  /** 总容量（<=0 不限额） */
  total_bytes: number;
  /** 剩余容量 */
  remain_bytes: number;
  /** 已用占比百分比；接口异常或未返回时前端展示兜底文案。 */
  used_percent?: number | string | null;
  /** 团队状态 1正常/2解散/15激活中/20回落/30退款 */
  status: number;
  /** 订阅到期 unix；0=无 */
  expire_at: number;
  /** 0/1 是否曾付费 */
  ever_paid: 0 | 1;
  /** 有效席位数 */
  seat_effective: number;
  /** free / paid / paid_fallback */
  package_status: 'free' | 'paid' | 'paid_fallback';
  /** 是否免费团队 */
  is_free: boolean;
  /** 是否黑金团队 */
  is_black_gold?: boolean;
  /** 是否已开启联系邮箱能力 */
  contact_email_enabled?: boolean;
  /** 0/1 合规锁 */
  compliance_hold: 0 | 1;
}

// ========== 官方资产库 API 类型 ==========
export interface OfficialAssetListData<T> {
  list: T[];
}

export interface OfficialAssetServerCategory {
  code: string;
  name: string;
  iconname: string;
  sort: number;
}

export interface OfficialAssetServerGenre {
  id: number;
  name: string;
  default_name: string;
  genre: string;
}

export interface OfficialAssetServerTagOption {
  name: string;
  value: string;
}

export interface OfficialAssetServerTagGroup {
  key: string;
  label: string;
  options: OfficialAssetServerTagOption[];
}

export interface OfficialAssetServerImage {
  url: string;
  width: number;
  height: number;
}

export interface OfficialAssetServerMaterial {
  id: number;
  name: string;
  description: string;
  tags: string[] | null;
  is_vip: string;
  is_free: string;
  main_image: OfficialAssetServerImage;
  three_view_image?: OfficialAssetServerImage;
  detail_image?: OfficialAssetServerImage;
  four_view_image?: OfficialAssetServerImage;
  wearing_image?: OfficialAssetServerImage;
  nine_grid_image?: OfficialAssetServerImage;
  top_view_image?: OfficialAssetServerImage;
  panorama_image?: OfficialAssetServerImage;
}

export interface OfficialAssetMaterialListData {
  cursor: string;
  list: OfficialAssetServerMaterial[];
  total?: number;
}

export interface OfficialAssetMaterialInfoData {
  info?: OfficialAssetServerMaterial;
}

export interface OfficialAssetMaterialListParams {
  genre_id: number;
  count: number;
  cursor?: string;
  filters?: Record<string, string[]>;
  keyword?: string;
}

// ========== Seedance API 类型 ==========

/** Seedance 任务模式 */
export type SeedanceTaskMode = 'reference' | 'first_frame' | 'first_last_frame';

/** Seedance 提交任务请求体（不含 webapi 公共参数，由请求拦截器注入） */
export interface SeedanceSubmitParams {
  task_mode: SeedanceTaskMode;
  init_images?: string[];
  reference_image_urls?: string[];
  /** 真人人像合规生成使用的已审核参考图字段，兼容 Agent 工具入参 */
  image_list?: string[];
  reference_video_urls?: string[];
  reference_audio_urls?: string[];
  /** 活体检测成功后服务端返回的人像资产组，合规生成人像视频时必传 */
  asset_group_id?: string;
  prompt?: string;
  ratio?: '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | '21:9' | 'adaptive';
  resolution?: '480p' | '720p';
  /** 4–15 为固定秒数，-1 为智能时长 */
  video_duration?: number;
  is_reference_image?: boolean;
}

/** Seedance 任务详情中的业务参数快照 */
export interface SeedanceTaskArgs {
  task_mode?: SeedanceTaskMode;
  init_images?: string[];
  reference_image_urls?: string[];
  /** 真人人像合规生成使用的已审核参考图字段，兼容 Agent 工具入参 */
  image_list?: string[];
  reference_video_urls?: string[];
  reference_audio_urls?: string[];
  /** 活体检测成功后服务端返回的人像资产组 */
  asset_group_id?: string;
  prompt?: string;
  ratio?: string;
  resolution?: string;
  video_duration?: number;
  is_reference_image?: boolean;
}

/** Seedance 单条任务（query/list 返回的 list 项） */
export interface SeedanceTaskItem {
  id: string;
  fail_msg: string;
  status: number;
  /**
   * 业务进度：1 任务创建成功，30 投递开放平台，50 开放平台处理成功；
   * 90/91/92/93/95/99 失败类终态；100 全部执行成功；2/3 为历史兼容成功/失败。
   */
  task_status: number;
  cover_url?: string;
  result_url?: string;
  result_videos?: string[];
  args?: SeedanceTaskArgs;
  created_at: number;
}

/** Seedance 列表接口 data */
export interface SeedanceListData {
  cursor: number;
  list: SeedanceTaskItem[];
}

/** Seedance 真人人像活体检测 session 创建响应 */
export interface SeedanceLivenessSessionCreateResult {
  session_id: string;
  qr_url: string;
}

/** Seedance 真人人像活体检测 session 状态响应 */
export interface SeedanceLivenessSessionStatusResult {
  status: string;
  group_id?: string;
  message?: string;
  fail_msg?: string;
}

/** Seedance 真人人像 H5 回跳结果落库响应 */
export interface SeedanceLivenessNotifyResult extends SeedanceLivenessSessionStatusResult {
  session_id?: string;
  pass?: boolean;
}

/** Seedance 真人人像文件夹创建参数 */
export interface SeedancePortraitFolderCreateParams {
  name: string;
  session_id?: string;
}

/** Seedance 真人人像文件夹条目 */
export interface SeedancePortraitFolderItem {
  folder_id: number;
  name: string;
  group_id?: string;
  cover_url?: string;
  /** 文件夹下的人像图片，包含同人校验状态；后端不返回时前端进入空态上传。 */
  items?: SeedancePortraitUploadItem[];
  item_count?: number;
  created_at?: number;
  updated_at?: number;
  [key: string]: any;
}

/** Seedance 真人人像文件夹列表响应 */
export interface SeedancePortraitFolderListData {
  list?: SeedancePortraitFolderItem[];
}

/** Seedance 真人人像文件夹删除参数 */
export interface SeedancePortraitFolderDeleteParams {
  folder_id: number;
}

/** Seedance 真人人像文件夹重命名参数 */
export interface SeedancePortraitFolderRenameParams {
  folder_id: number;
  name: string;
}

/** Seedance 合规素材库 tab 类型；只有 portrait 支持 folder_id。 */
export type SeedanceComplianceLibraryTabType = 'portrait' | 'image' | 'video' | 'audio';

/** Seedance 合规素材库列表参数 */
export interface SeedanceComplianceLibraryListParams {
  tab_type: SeedanceComplianceLibraryTabType;
  folder_id?: number;
  page?: number;
  page_size?: number;
}

/** Seedance 真人人像参考图上传参数 */
export interface SeedancePortraitUploadParams {
  folder_id: number;
  urls: string[];
}

export type SeedancePortraitUploadStatus = 'pending' | 'checking' | 'pass' | 'fail';

export type SeedanceComplianceUploadStatus = SeedancePortraitUploadStatus;

/** Seedance 真人人像参考图条目 */
export interface SeedancePortraitUploadItem {
  item_id?: string;
  url: string;
  audit_status?: SeedancePortraitUploadStatus;
  upload_id?: string;
  fail_message?: string;
  [key: string]: any;
}

/** Seedance 合规素材库普通素材条目 */
export interface SeedanceComplianceLibraryItem {
  id: string | number;
  tab_type: SeedanceComplianceLibraryTabType;
  material_type?: 'image' | 'video' | 'audio';
  folder_id?: number;
  url: string;
  /** 视频压缩地址，普通图/音素材不会返回。 */
  compress_url?: string;
  /** 视频水印压缩地址，普通图/音素材不会返回。 */
  watermark_compress_url?: string;
  thumbnail_url?: string;
  width?: number;
  height?: number;
  duration?: number;
  audit_status?: SeedanceComplianceUploadStatus;
  fail_message?: string;
  created_at?: number;
  updated_at?: number;
  [key: string]: any;
}

/** Seedance 合规素材库列表响应 */
export interface SeedanceComplianceLibraryListData {
  list?: SeedanceComplianceLibraryItem[];
}

/** Seedance 真人人像参考图上传响应；提交后服务端异步做人像校验。 */
export interface SeedancePortraitUploadResult {
  upload_id: string;
  status: SeedancePortraitUploadStatus;
}

/** Seedance 合规素材上传状态查询参数 */
export interface SeedanceComplianceUploadStatusParams {
  upload_id: string;
}

/** Seedance 合规素材上传状态查询响应 */
export interface SeedanceComplianceUploadStatusResult {
  status: SeedanceComplianceUploadStatus;
  upload_id?: string;
  item_ids?: string[];
  fail_message?: string;
}

/** Seedance 普通图/视/音素材上传参数；按文档约束不能携带 folder_id。 */
export interface SeedanceComplianceLibraryUploadParams {
  tab_type: Exclude<SeedanceComplianceLibraryTabType, 'portrait'>;
  urls: string[];
  /** 视频素材压缩地址，按 urls 顺序传递给后端。 */
  compress_url?: string[];
}

/** Seedance 普通图/视/音素材上传响应；提交后复用 upload/status 轮询。 */
export interface SeedanceComplianceLibraryUploadResult {
  upload_id: string;
  status: SeedanceComplianceUploadStatus;
}

/** Seedance 合规素材删除参数 */
export interface SeedanceComplianceLibraryDeleteParams {
  item_id: number;
}
